/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, memo } from "react";
import { DownloadButtons } from "./constants";
import SummaryCard from "../../../components/Classroom/StudentClassroom/SummaryCard";
import LastWeekCharts from "../../../components/PerformanceComponents/LastWeekCharts";
import PracticeAccordion from "../../../components/PerformanceComponents/PracticeAccordion";
import TopicCompletionChart from "../../../components/PerformanceComponents/TopicCompletionChart";
import { getSummaryDetails } from "../../../utils/dashboard";
import { makeAuthenticatedRequest } from "../../../utils/api";
import Loader from "../../../components/Loader";
import "./print.scss";
import { downloadPdf } from "./hooks";
import { getAttempts, getIeltsAttempts } from "../../../utils/practices";
import DashboardDropdown from "../../../components/StudentDashboard/Dropdown/dropdown"; // Import your DashboardDropdown
import ScoreBandSwitch from "../../../components/PerformanceComponents/ScoreBandSwitch";
import InfoTooltip from "./InfoTooltip";
import RecentMistakesWidget from "../../../components/StudentDashboard/WroongQuestionsReport/RecentMistakesWidget";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {getUserRole} from "../../../store/userRole";

function OverallPerformance({ classroomId, isMobileView, studentId, onAssessmentDataChange, hasIeltsExams, subjectsMap }) {
  const [isLoading, setIsLoading] = useState(false);
  const [summaryDetails, setSummaryDetails] = useState({});
  const navigate = useNavigate()
  const roleOfTheUser = useSelector(getUserRole);

  // States for Charts
  const [chartsPracticeAttempts, setChartsPracticeAttempts] = useState([]);
  const [chartsTestAttempts, setChartsTestAttempts] = useState([]);
  const [chartsAssessmentAttempts, setChartsAssessmentAttempts] = useState([]);
  const [chartsFilter, setChartsFilter] = useState("week");
  const [chartActiveTab, setChartActiveTab] = useState("practice");
  const [selectedView, setSelectedView] = useState('score')

  // States for Accordion
  const [accordionPracticeAttempts, setAccordionPracticeAttempts] = useState(
    []
  );
  const [accordionTestAttempts, setAccordionTestAttempts] = useState([]);
  const [accordionAssessmentAttempts, setAccordionAssessmentAttempts] =
    useState([]);
  const [accordionFilter, setAccordionFilter] = useState("week");
  const [activeTab, setActiveTab] = useState("practice");

  const [heading, setHeading] = useState("Your last week's performance");

  const [topicCompletion, setTopicCompletion] = useState(null);

  const getStartDateBasedOnFilter = (filter) => {
    const now = new Date();
    if (filter === "today") {
      return new Date(now.setDate(now.getDate() - 1)).toISOString(); // Last 24 hours
    } else if (filter === "week") {
      return new Date(now.setDate(now.getDate() - 6)).toISOString(); // Last 6 days
    } else if (filter === "month") {
      return new Date(now.setMonth(now.getMonth() - 1)).toISOString(); // Last month
    } else if (filter === "year") {
      return new Date(now.setFullYear(now.getFullYear() - 1)).toISOString(); // Last year
    }
    return new Date().toISOString();
  };

  // Separate data fetching for charts
  const fetchChartsData = useCallback(async () => {
    setIsLoading(true);
    try {
      const startDate = getStartDateBasedOnFilter(chartsFilter);
      const [practiceResponse, testResponse, assessmentResponse] =
        await Promise.all([
          getAttempts(classroomId, startDate, "practice", studentId),
          getAttempts(classroomId, startDate, "test", studentId),
          hasIeltsExams ? getIeltsAttempts(classroomId, startDate, studentId) : getAttempts(classroomId, startDate, "assessment", studentId),
        ]);

      if (practiceResponse.ok) {
        setChartsPracticeAttempts(practiceResponse.data.examAttempts);
      }
      if (testResponse.ok) {
        setChartsTestAttempts(testResponse.data.examAttempts);
      }
      if (assessmentResponse.ok) {
        setChartsAssessmentAttempts(assessmentResponse.data.examAttempts);
      }
    } catch (error) {
      console.error("Error fetching charts data:", error.message);
    } finally {
      setIsLoading(false);
    }
  }, [classroomId, studentId, chartsFilter]);

  // Separate data fetching for accordion
  const fetchAccordionData = useCallback(async () => {
    setIsLoading(true);
    try {
      const startDate = getStartDateBasedOnFilter(accordionFilter);
      const [practiceResponse, testResponse, assessmentResponse] =
        await Promise.all([
          getAttempts(classroomId, startDate, "practice", studentId),
          getAttempts(classroomId, startDate, "test", studentId),
          hasIeltsExams ? getIeltsAttempts(classroomId, startDate, studentId) : getAttempts(classroomId, startDate, "assessment", studentId),
        ]);

      if (practiceResponse.ok) {
        setAccordionPracticeAttempts(practiceResponse.data.examAttempts);
      }
      if (testResponse.ok) {
        setAccordionTestAttempts(testResponse.data.examAttempts);
      }
      if (assessmentResponse.ok) {
        setAccordionAssessmentAttempts(assessmentResponse.data.examAttempts);
      }
    } catch (error) {
      console.error("Error fetching accordion data:", error.message);
    } finally {
      setIsLoading(false);
    }
  }, [classroomId, studentId, accordionFilter]);

  // Fetch summary details separately
  const fetchSummaryDetails = useCallback(async () => {
    try {
      const summaryResponse = await getSummaryDetails(classroomId, studentId);
      if (summaryResponse.ok) {
        setSummaryDetails(summaryResponse.data);
      }
    } catch (error) {
      console.error("Error fetching summary details:", error.message);
    }
  }, [classroomId, studentId]);

  // Fetch topic completion data
  const fetchTopicCompletion = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest(
        `/topics/${classroomId}/percentagePracticeAndTestDone${studentId ? `?studentId=${studentId}` : ''}`,
        "GET",
        null
      );

      if (response.ok) {
        const data = await response.json();
        setTopicCompletion(data?.topics);
      }
    } catch (error) {
      console.error("Error fetching topic completion:", error);
    }
  }, [classroomId, studentId]);

  // Effect for charts data
  useEffect(() => {
    if (classroomId) {
      fetchChartsData();
    }
  }, [classroomId, chartsFilter]);

  // Effect for accordion data
  useEffect(() => {
    if (classroomId) {
      fetchAccordionData();
    }
  }, [classroomId, accordionFilter]);

  // Effect for summary details
  useEffect(() => {
    if (classroomId) {
      fetchSummaryDetails();
    }
  }, [classroomId]);

  // Add to existing useEffect
  useEffect(() => {
    if (classroomId) {
      fetchTopicCompletion();
    }
  }, [classroomId, fetchTopicCompletion]);

  useEffect(() => {
    DownloadButtons[0].onClick = () => downloadPdf("Overall-Performance");
  }, [downloadPdf]);

  useEffect(() => {
    if (chartsFilter === "today") {
      setHeading("Your performance today");
    } else if (chartsFilter === "week") {
      setHeading("Your last week's performance");
    } else if (chartsFilter === "month") {
      setHeading("Your last month's performance");
    } else if (chartsFilter === "year") {
      setHeading("Your last year's performance");
    }
  }, [chartsFilter]);

  // Notify parent component when assessment data changes (for PDF report)
  useEffect(() => {
    if (onAssessmentDataChange && chartsAssessmentAttempts.length > 0) {
      onAssessmentDataChange(chartsAssessmentAttempts);
    }
  }, [chartsAssessmentAttempts, onAssessmentDataChange]);

  if (isLoading) {
    return <Loader />;
  }

  const summaryCards = [
    {
      image: "/styled-playbutton.svg",
      value: summaryDetails?.totalQ_Answered,
      description: "Questions Answered",
    },
    {
      image: "/styled-tickbox.svg",
      value: summaryDetails?.timeSpentPractice,
      description: "Time Spent Practicing",
    },
    {
      image: "/styles-usersicons.svg",
      value: summaryDetails?.timeSpentTest,
      description: "Time Spent on Tests",
    },
    {
      image: "/styled-winingcup.svg",
      value: summaryDetails?.practiceAttempted,
      description: "Practice Sets Attempted",
    },
    {
      image: "/styled-winingcup.svg",
      value: summaryDetails?.testAttempted,
      description: "Topic-wise Tests Attempted",
    },
    {
      image: "/styled-winingcup.svg",
      value: summaryDetails?.assessmentAttempted,
      description: "Full Length Exams Attempted",
    },
  ];

  return (
    <div
      style={{ margin: "10px", paddingBottom: isMobileView ? "20px" : "" }}
      id="Overall-Performance"
    >
      <div className="row">
        {summaryCards.map((card, index) => (
          <div
            key={index}
            className="col-6 col-lg-3"
            style={{ marginBottom: "24px" }}
          >
            <SummaryCard
              imageURl={card.image}
              isMobileView={isMobileView}
              value={card.value}
              description={card.description}
            />
          </div>
        ))}
      </div>

      {/* Topic Completion Chart */}
      <div className="row px-3">
        <div className="col-12">
          <h5 className="topic-completion-heading">
            Topics Completion Overview
          </h5>
          <TopicCompletionChart
            data={topicCompletion}
            classroomId={classroomId}
            studentId={studentId}
          />
        </div>
      </div>
      <div style={{margin: '0px 18px 24px 18px'}}>
        <RecentMistakesWidget
          studentId={studentId}
          classroomId={classroomId}
          onViewAll={() => {
            navigate(`${roleOfTheUser === "Student" ? "/student" : ""}/myClassrooms/${classroomId}/dashboard/wrong-questions${roleOfTheUser === "Student" ? "" : `?studentId=${studentId}`}`)
          }}
          isProgressPage
          subjectsMap={subjectsMap}
          hasIeltsExams={hasIeltsExams}
        />
      </div>
      {/* Filter Dropdown with Custom DashboardDropdown */}
      <div
        className="dashboardThisWeek d-flex align-items-center justify-content-end w-100 flex-wrap"
        style={{ gap: "8px" }}
      >
        <span>
          {chartsFilter.charAt(0).toUpperCase() + chartsFilter.slice(1)}
        </span>
        <DashboardDropdown onItemSelect={setChartsFilter} />
      </div>

      <div
        className="row px-3"
        style={{ display: `${isLoading ? "none" : ""}` }}
      >
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center'
        }}>
          <h5>{heading}</h5>
          {hasIeltsExams && (
            <>
              <ScoreBandSwitch value={selectedView} onChange={setSelectedView} isMobileView={isMobileView} />
              <InfoTooltip placement="bottom" id="score-band-info">
                <div style={{ textAlign: "left" }}>
                  <strong>Score</strong> shows your Listening and Reading results.
                  <br />
                  <strong>Band</strong> shows your Writing and Speaking scores.
                </div>
              </InfoTooltip>
            </>
          )}
        </div>
        <LastWeekCharts
          practiceAttempts={chartsPracticeAttempts}
          testAttempts={chartsTestAttempts}
          assessmentAttempts={chartsAssessmentAttempts}
          loading={isLoading}
          filter={chartsFilter}
          keyType={chartActiveTab}
          onTabChange={setChartActiveTab}
          hasIeltsExams={hasIeltsExams}
          selectedView={selectedView}
        />
      </div>

      <div
        className="row px-3 mt-3"
        style={{ display: `${isLoading ? "none" : ""}` }}
      >
        <PracticeAccordion
          practiceAttempts={accordionPracticeAttempts}
          testAttempts={accordionTestAttempts}
          assessmentAttempts={accordionAssessmentAttempts}
          filter={accordionFilter}
          onFilterChange={setAccordionFilter}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          hasIeltsExams={hasIeltsExams}
        />
      </div>
    </div>
  );
}

export default memo(OverallPerformance);
